import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Play, Pause, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { formatDuration } from "@/lib/voice";
import { useLang } from "@/lib/i18n";

/**
 * Play-bar for a voice chat message. Streams from an authenticated backend
 * endpoint — never a direct storage URL.
 *
 * `authQuery`  → appended to the audio URL when the caller is a customer
 *                (browsers cannot set headers on <audio src>). Example: `?token=xxx`.
 * `authHeader` → Bearer token for technician-side; downloaded as a blob so
 *                the header can be sent.
 */
export function VoiceBubble({ audioUrl, duration, contentType, authQuery, authHeader, testid }) {
  const { t } = useLang();
  const audioRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [blobUrl, setBlobUrl] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const resolveSrc = async () => {
    if (blobUrl) return blobUrl;
    // Customer flow — the audio URL already carries an auth query, use <audio>
    // src directly. Technician flow — fetch as blob with Authorization header.
    if (authHeader && !authQuery) {
      setLoading(true);
      try {
        const base = API.replace(/\/api$/, "");
        const res = await axios.get(`${base}${audioUrl}`, {
          headers: { Authorization: authHeader },
          responseType: "blob",
        });
        const b = URL.createObjectURL(new Blob([res.data], { type: contentType || res.data.type || "audio/webm" }));
        setBlobUrl(b);
        return b;
      } catch {
        setErrored(true);
        return null;
      } finally {
        setLoading(false);
      }
    }
    const base = API.replace(/\/api$/, "");
    return `${base}${audioUrl}${authQuery || ""}`;
  };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      return;
    }
    if (!a.src) {
      const src = await resolveSrc();
      if (!src) return;
      a.src = src;
    }
    try {
      await a.play();
    } catch {
      setErrored(true);
    }
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a || !a.duration || !isFinite(a.duration)) return;
    setProgress(a.currentTime / a.duration);
  };

  const total = Math.max(1, Math.round(duration || 0));
  const currentSec = Math.round(progress * total);

  return (
    <div
      className="flex items-center gap-2 min-w-[180px]"
      data-testid={testid || "voice-bubble"}
    >
      <button
        type="button"
        onClick={toggle}
        disabled={loading || errored}
        aria-label={playing ? t("voice_pause_tt") : t("voice_play_tt")}
        data-testid="voice-play"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 disabled:opacity-50"
        style={{ background: "var(--accent)", color: "#000" }}
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : playing ? <Pause size={14} /> : <Play size={14} />}
      </button>
      <div className="flex-1 min-w-0">
        <div
          className="h-1 rounded-full overflow-hidden"
          style={{ background: "rgba(255,255,255,0.14)" }}
        >
          <div
            className="h-full transition-[width] duration-150 ease-linear"
            style={{ width: `${Math.round(progress * 100)}%`, background: "var(--accent)" }}
          />
        </div>
        <div className="mt-1 text-[10px] tabular-nums text-white/60">
          {playing || progress > 0 ? formatDuration(currentSec) : formatDuration(total)}
          {errored && <span className="ml-2 text-red-400">{t("voice_unavailable")}</span>}
        </div>
      </div>
      <audio
        ref={audioRef}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={onTime}
        onError={() => { setErrored(true); setPlaying(false); }}
        data-testid="voice-audio-el"
      />
    </div>
  );
}
